import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Eye, MousePointer, FolderOpen, LogOut, Sun, TrendingUp, TrendingDown,
  Percent, Lightbulb, Users, Clock, Search, X, ChevronDown,
  Mail, Phone, MapPin, Building, Target, DollarSign, MessageSquare
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_CONFIG = {
  novo: { label: 'Novo', bg: 'bg-blue-500/10', text: 'text-blue-400', dot: 'bg-blue-400' },
  contactado: { label: 'Contactado', bg: 'bg-amber-500/10', text: 'text-amber-400', dot: 'bg-amber-400' },
  em_analise: { label: 'Em análise', bg: 'bg-purple-500/10', text: 'text-purple-400', dot: 'bg-purple-400' },
  fechado: { label: 'Fechado', bg: 'bg-emerald-500/10', text: 'text-emerald-400', dot: 'bg-emerald-400' },
};

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [stats, setStats] = useState({
    totalVisits: 0, todayVisits: 0, ctaClicks: 0, portfolioClicks: 0,
    conversionRate: 0, dailyVisits: [], variations: { visits: 0, cta: 0, portfolio: 0 },
  });

  // Leads state
  const [leadsData, setLeadsData] = useState({ leads: [], total: 0, todayCount: 0, lastLeadDate: '' });
  const [leadsFilter, setLeadsFilter] = useState('all');
  const [leadsSearch, setLeadsSearch] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [statusDropdown, setStatusDropdown] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('loom-admin-token');
    if (token) setIsAuthenticated(true);
  }, []);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/stats`);
      if (res.ok) setStats(await res.json());
    } catch (err) { console.error(err); }
  };

  const fetchLeads = async () => {
    try {
      const params = new URLSearchParams({ filter: leadsFilter });
      if (leadsSearch.trim()) params.set('search', leadsSearch.trim());
      const res = await fetch(`${BACKEND_URL}/api/admin/leads?${params}`);
      if (res.ok) setLeadsData(await res.json());
    } catch (err) { console.error(err); }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchStats();
      fetchLeads();
      const interval = setInterval(() => { fetchStats(); fetchLeads(); }, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) fetchLeads();
  }, [leadsFilter, leadsSearch]);

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/leads/${leadId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      setStatusDropdown(null);
      fetchLeads();
    } catch (err) { console.error(err); }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData),
      });
      if (res.ok) {
        localStorage.setItem('loom-admin-token', 'authenticated');
        setIsAuthenticated(true); setLoginError('');
      } else { setLoginError('Credenciais inválidas'); }
    } catch { setLoginError('Erro de ligação'); }
  };

  const handleLogout = () => {
    localStorage.removeItem('loom-admin-token');
    setIsAuthenticated(false);
  };

  const insights = useMemo(() => {
    const list = [];
    const { totalVisits, ctaClicks, portfolioClicks, conversionRate, variations } = stats;
    if (conversionRate > 5) list.push({ type: 'positive', text: `A taxa de conversão de ${conversionRate}% está acima da média. O CTA está a funcionar bem.` });
    else if (totalVisits > 10 && conversionRate < 2) list.push({ type: 'warning', text: 'A taxa de conversão está baixa. Considere tornar o CTA mais visível ou mudar o copy.' });
    if (variations.visits > 20) list.push({ type: 'positive', text: `As visitas cresceram ${variations.visits}% vs semana passada.` });
    else if (variations.visits < -10) list.push({ type: 'warning', text: `As visitas caíram ${Math.abs(variations.visits)}% vs semana passada.` });
    if (portfolioClicks > 0 && ctaClicks === 0) list.push({ type: 'info', text: 'Visitantes veem o portfólio mas não agendam. Adicione um CTA mais forte.' });
    if (totalVisits > 0 && ctaClicks > 0 && portfolioClicks > 0) list.push({ type: 'positive', text: 'O funil está ativo — visitantes interagem com o portfólio e o CTA.' });
    if (list.length === 0) list.push({ type: 'info', text: 'Continue a promover o site para gerar dados suficientes para insights.' });
    return list;
  }, [stats]);

  const formatDate = (d) => {
    if (!d) return '-';
    const date = new Date(d);
    return date.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatRelative = (d) => {
    if (!d) return '-';
    const diff = Date.now() - new Date(d).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `Há ${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `Há ${hours}h`;
    return `Há ${Math.floor(hours / 24)}d`;
  };

  // --- Login ---
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#111113] flex items-center justify-center px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <div className="bg-[#1a1a1e] rounded-2xl p-8 border border-white/5">
            <div className="text-center mb-8">
              <img src="/logo-loom-white.png" alt="Loom" className="h-16 mx-auto mb-6" />
              <h1 data-testid="login-title" className="font-['Syne'] text-xl font-bold text-white">Dashboard</h1>
            </div>
            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className="text-xs text-white/40 mb-1.5 block">Email</label>
                <input data-testid="login-email" type="email" value={loginData.email}
                  onChange={(e) => setLoginData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full bg-[#111113] border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:border-[#ff00c7] outline-none transition-colors"
                  placeholder="admin@loom.studio" />
              </div>
              <div>
                <label className="text-xs text-white/40 mb-1.5 block">Password</label>
                <input data-testid="login-password" type="password" value={loginData.password}
                  onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                  className="w-full bg-[#111113] border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:border-[#ff00c7] outline-none transition-colors"
                  placeholder="••••••••" />
              </div>
              {loginError && <p data-testid="login-error" className="text-red-400 text-sm">{loginError}</p>}
              <button data-testid="login-submit" type="submit"
                className="w-full bg-[#ff00c7] text-white py-3 rounded-xl font-semibold text-sm hover:bg-[#ff00c7]/90 transition-colors">
                Entrar
              </button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  const VariationBadge = ({ value }) => {
    if (value === 0) return null;
    const isPositive = value > 0;
    return (
      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${isPositive ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
        {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
        {isPositive ? '+' : ''}{value}%
      </span>
    );
  };

  const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-[#242428] border border-white/10 rounded-lg px-3 py-2 shadow-xl">
        <p className="text-xs text-white/50">{label}</p>
        <p className="text-sm font-semibold text-white">{payload[0].value} visitas</p>
      </div>
    );
  };

  const metricCards = [
    { id: 'visits', label: 'Website Visits', value: stats.totalVisits, sub: `${stats.todayVisits} hoje`, icon: Eye, color: '#3b82f6', variation: stats.variations?.visits },
    { id: 'cta', label: 'Book a Meeting', value: stats.ctaClicks, sub: 'cliques totais', icon: MousePointer, color: '#ff00c7', variation: stats.variations?.cta },
    { id: 'portfolio', label: 'Portfolio', value: stats.portfolioClicks, sub: 'cliques totais', icon: FolderOpen, color: '#10b981', variation: stats.variations?.portfolio },
    { id: 'conversion', label: 'Taxa de Conversão', value: `${stats.conversionRate || 0}%`, sub: 'reuniões / visitas', icon: Percent, color: '#f59e0b', variation: null },
  ];

  const insightStyles = {
    positive: { bg: 'bg-emerald-500/5', border: 'border-emerald-500/10', dot: 'bg-emerald-400' },
    warning: { bg: 'bg-amber-500/5', border: 'border-amber-500/10', dot: 'bg-amber-400' },
    info: { bg: 'bg-blue-500/5', border: 'border-blue-500/10', dot: 'bg-blue-400' },
  };

  const filterOptions = [
    { value: 'all', label: 'Todos' },
    { value: 'today', label: 'Hoje' },
    { value: '7days', label: '7 dias' },
    { value: '30days', label: '30 dias' },
  ];

  return (
    <div className="min-h-screen bg-[#111113]">
      <header className="border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-loom-white.png" alt="Loom" className="h-12" />
            <span className="font-['Syne'] text-base font-bold text-white">Dashboard</span>
          </div>
          <button data-testid="logout-btn" onClick={handleLogout}
            className="flex items-center gap-2 text-white/40 hover:text-white text-sm transition-colors">
            <LogOut className="w-4 h-4" />Sair
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        {/* Today */}
        <div className="flex items-center gap-2 mb-8">
          <Sun className="w-4 h-4 text-yellow-400" />
          <span data-testid="today-badge" className="text-sm text-white/50">
            Hoje: {stats.todayVisits} visita{stats.todayVisits !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {metricCards.map((card, index) => (
            <motion.div key={card.id} data-testid={`stat-card-${card.id}`}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.08 }}
              className="group bg-[#1a1a1e] rounded-2xl p-5 border border-white/[0.04] hover:border-white/[0.08] transition-all duration-300 hover:shadow-lg hover:shadow-black/20">
              <div className="flex items-start justify-between mb-4">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${card.color}12` }}>
                  <card.icon className="w-4 h-4" style={{ color: card.color }} />
                </div>
                {card.variation != null && <VariationBadge value={card.variation} />}
              </div>
              <p data-testid={`stat-value-${card.id}`} className="font-['Syne'] text-3xl font-bold text-white mb-1">{card.value}</p>
              <p className="text-xs text-white/35">{card.label}</p>
              <p className="text-[11px] text-white/20 mt-0.5">{card.sub}</p>
            </motion.div>
          ))}
        </div>

        {/* Chart */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
          data-testid="visits-chart" className="bg-[#1a1a1e] rounded-2xl p-6 border border-white/[0.04] mb-8">
          <h3 className="font-['Syne'] text-sm font-semibold text-white mb-1">Visitas — Últimos 7 dias</h3>
          <p className="text-xs text-white/30 mb-5">Visualizações diárias do site</p>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats.dailyVisits || []} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
                <defs>
                  <linearGradient id="visitGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ff00c7" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#ff00c7" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: 'rgba(255,255,255,0.25)', fontSize: 11 }} />
                <YAxis axisLine={false} tickLine={false} allowDecimals={false} tick={{ fill: 'rgba(255,255,255,0.2)', fontSize: 11 }} />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="visits" stroke="#ff00c7" strokeWidth={2}
                  fill="url(#visitGrad)" dot={false} activeDot={{ r: 4, fill: '#ff00c7', stroke: '#111113', strokeWidth: 2 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Insights */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}
          data-testid="insights-section" className="bg-[#1a1a1e] rounded-2xl p-6 border border-white/[0.04] mb-10">
          <div className="flex items-center gap-2 mb-5">
            <Lightbulb className="w-4 h-4 text-amber-400" />
            <h3 className="font-['Syne'] text-sm font-semibold text-white">Insights</h3>
          </div>
          <div className="space-y-3">
            {insights.map((insight, i) => {
              const s = insightStyles[insight.type];
              return (
                <div key={i} className={`${s.bg} border ${s.border} rounded-xl px-4 py-3 flex items-start gap-3`}>
                  <div className={`w-1.5 h-1.5 rounded-full ${s.dot} mt-1.5 flex-shrink-0`} />
                  <p className="text-sm text-white/70 leading-relaxed">{insight.text}</p>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* ========== LEADS SECTION ========== */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}>
          <div className="flex items-center gap-2 mb-6">
            <Users className="w-5 h-5 text-[#ff00c7]" />
            <h2 className="font-['Syne'] text-lg font-bold text-white">Leads Recebidos</h2>
          </div>

          {/* Lead Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div data-testid="leads-total-card" className="bg-[#1a1a1e] rounded-2xl p-5 border border-white/[0.04]">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#ff00c7]/10 mb-3">
                <Users className="w-4 h-4 text-[#ff00c7]" />
              </div>
              <p className="font-['Syne'] text-2xl font-bold text-white">{leadsData.total}</p>
              <p className="text-xs text-white/35">Total de leads</p>
            </div>
            <div data-testid="leads-today-card" className="bg-[#1a1a1e] rounded-2xl p-5 border border-white/[0.04]">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-blue-500/10 mb-3">
                <Sun className="w-4 h-4 text-blue-400" />
              </div>
              <p className="font-['Syne'] text-2xl font-bold text-white">{leadsData.todayCount}</p>
              <p className="text-xs text-white/35">Leads hoje</p>
            </div>
            <div data-testid="leads-last-card" className="bg-[#1a1a1e] rounded-2xl p-5 border border-white/[0.04]">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-emerald-500/10 mb-3">
                <Clock className="w-4 h-4 text-emerald-400" />
              </div>
              <p className="font-['Syne'] text-lg font-bold text-white">{formatRelative(leadsData.lastLeadDate)}</p>
              <p className="text-xs text-white/35">Último lead</p>
            </div>
          </div>

          {/* Filters & Search */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="flex gap-2">
              {filterOptions.map(opt => (
                <button key={opt.value} onClick={() => setLeadsFilter(opt.value)}
                  data-testid={`leads-filter-${opt.value}`}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    leadsFilter === opt.value
                      ? 'bg-[#ff00c7]/10 text-[#ff00c7] border border-[#ff00c7]/20'
                      : 'bg-[#1a1a1e] text-white/40 border border-white/[0.04] hover:text-white/60'
                  }`}>
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="relative flex-1 sm:max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" />
              <input data-testid="leads-search" type="text" value={leadsSearch}
                onChange={(e) => setLeadsSearch(e.target.value)}
                placeholder="Pesquisar nome, marca ou email..."
                className="w-full bg-[#1a1a1e] border border-white/[0.04] rounded-lg pl-9 pr-4 py-2 text-sm text-white placeholder-white/20 focus:border-[#ff00c7]/30 outline-none transition-colors" />
            </div>
          </div>

          {/* Leads Table */}
          <div className="bg-[#1a1a1e] rounded-2xl border border-white/[0.04] overflow-hidden mb-10">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="leads-table">
                <thead>
                  <tr className="border-b border-white/[0.04]">
                    {['Nome', 'Marca', 'Email', 'Telefone', 'Orçamento', 'Status', 'Data', ''].map(h => (
                      <th key={h} className="text-left text-[11px] text-white/30 font-medium px-4 py-3 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {leadsData.leads.length === 0 ? (
                    <tr><td colSpan={8} className="px-4 py-12 text-center text-white/30 text-sm">Nenhum lead encontrado</td></tr>
                  ) : (
                    leadsData.leads.map((lead, idx) => {
                      const st = STATUS_CONFIG[lead.status] || STATUS_CONFIG.novo;
                      return (
                        <tr key={lead.id || idx} className="border-b border-white/[0.02] hover:bg-white/[0.02] transition-colors" data-testid={`lead-row-${idx}`}>
                          <td className="px-4 py-3 text-sm text-white font-medium">{lead.name || '-'}</td>
                          <td className="px-4 py-3 text-sm text-white/60">{lead.business || '-'}</td>
                          <td className="px-4 py-3 text-sm text-white/60">{lead.email || '-'}</td>
                          <td className="px-4 py-3 text-sm text-white/60">{lead.phone || '-'}</td>
                          <td className="px-4 py-3 text-sm text-white/60">{lead.budget || '-'}</td>
                          <td className="px-4 py-3 relative">
                            <button onClick={() => setStatusDropdown(statusDropdown === (lead.id || idx) ? null : (lead.id || idx))}
                              data-testid={`lead-status-btn-${idx}`}
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${st.bg} ${st.text}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                              {st.label}
                              <ChevronDown className="w-3 h-3" />
                            </button>
                            {statusDropdown === (lead.id || idx) && (
                              <div className="absolute z-20 top-full left-4 mt-1 bg-[#242428] border border-white/10 rounded-xl shadow-2xl py-1 min-w-[140px]">
                                {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                                  <button key={key} onClick={() => updateLeadStatus(lead.id, key)}
                                    className="w-full text-left px-3 py-2 text-xs hover:bg-white/5 flex items-center gap-2 transition-colors">
                                    <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                                    <span className="text-white/70">{cfg.label}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-xs text-white/30">{formatDate(lead.createdAt)}</td>
                          <td className="px-4 py-3">
                            <button onClick={() => setSelectedLead(lead)} data-testid={`lead-details-btn-${idx}`}
                              className="text-[#ff00c7] text-xs hover:underline">Ver detalhes</button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>
      </main>

      {/* Lead Detail Modal */}
      <AnimatePresence>
        {selectedLead && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setSelectedLead(null)}>
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              data-testid="lead-detail-modal"
              className="bg-[#1a1a1e] rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto border border-white/5">
              <div className="p-5 border-b border-white/5 flex justify-between items-center sticky top-0 bg-[#1a1a1e] z-10">
                <div>
                  <h3 className="font-['Syne'] text-lg font-bold text-white">{selectedLead.name}</h3>
                  <p className="text-xs text-white/40">{formatDate(selectedLead.createdAt)}</p>
                </div>
                <button onClick={() => setSelectedLead(null)} className="text-white/30 hover:text-white transition-colors p-1">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-5 space-y-4">
                {[
                  { icon: Building, label: 'Marca / Negócio', value: selectedLead.business },
                  { icon: Target, label: 'Instagram / Website', value: selectedLead.social },
                  { icon: MapPin, label: 'Localização', value: selectedLead.location },
                  { icon: Mail, label: 'Email', value: selectedLead.email },
                  { icon: Phone, label: 'Telefone', value: selectedLead.phone },
                  { icon: DollarSign, label: 'Orçamento mensal', value: selectedLead.budget },
                ].map(item => (
                  <div key={item.label} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-white/[0.04] flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-4 h-4 text-[#ff00c7]" />
                    </div>
                    <div>
                      <p className="text-[11px] text-white/30 uppercase tracking-wider">{item.label}</p>
                      <p className="text-sm text-white">{item.value || '-'}</p>
                    </div>
                  </div>
                ))}

                {selectedLead.helpNeeded?.length > 0 && (
                  <div className="pt-3 border-t border-white/5">
                    <p className="text-[11px] text-white/30 uppercase tracking-wider mb-2">Área em que precisa de ajuda</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedLead.helpNeeded.map((s, i) => (
                        <span key={i} className="px-3 py-1 bg-[#ff00c7]/8 text-[#ff00c7] text-xs rounded-full border border-[#ff00c7]/10">{s}</span>
                      ))}
                    </div>
                  </div>
                )}

                {selectedLead.frustration && (
                  <div className="pt-3 border-t border-white/5">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-white/[0.04] flex items-center justify-center flex-shrink-0">
                        <MessageSquare className="w-4 h-4 text-amber-400" />
                      </div>
                      <div>
                        <p className="text-[11px] text-white/30 uppercase tracking-wider">Principal frustração</p>
                        <p className="text-sm text-white leading-relaxed">{selectedLead.frustration}</p>
                      </div>
                    </div>
                  </div>
                )}

                {selectedLead.desiredResult && (
                  <div className="pt-3 border-t border-white/5">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-white/[0.04] flex items-center justify-center flex-shrink-0">
                        <Target className="w-4 h-4 text-emerald-400" />
                      </div>
                      <div>
                        <p className="text-[11px] text-white/30 uppercase tracking-wider">Resultado esperado</p>
                        <p className="text-sm text-white leading-relaxed">{selectedLead.desiredResult}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
